import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import type {
  FeedbackActivity,
  FeedbackAnalysis,
  FeedbackEntry,
  FeedbackKind,
  FeedbackImplementationInput,
  FeedbackPage,
  FeedbackPostponementInput,
  FeedbackScreenshot,
  FeedbackSortDirection,
  FeedbackSortKey,
  FeedbackStatus,
  FeedbackDateField,
  FeedbackTimeGrouping,
} from './types'
import { useFeedbackApi } from '../feedback-api'

/**
 * Datenzugriff der Plattformfunktion.
 *
 * Der Client verwendet ausdrücklich `/api/feedback`, hängt das Zugriffstoken
 * an und übersetzt Fehlerantworten zentral.
 */

export interface FeedbackListParams {
  page: number
  pageSize: number
  status?: FeedbackStatus
  art?: FeedbackKind
  text?: string
  modul?: string
  ersteller?: string
  erstellerId?: string
  areas?: string
  creatorIds?: string
  nurUngelesen?: boolean
  angelegtVon?: string
  angelegtBis?: string
  scopeAreas?: string
  dateField?: FeedbackDateField
  dateFrom?: string
  dateTo?: string
  sortKey: FeedbackSortKey
  sortDirection: FeedbackSortDirection
}

/** Query-Schlüssel an einer Stelle: verhindert, dass Caches auseinanderlaufen. */
export const feedbackKeys = {
  root: ['feedback'] as const,
  all: ['feedback', 'eintraege'] as const,
  list: (params: FeedbackListParams) => [...feedbackKeys.all, 'list', params] as const,
  detail: (id: string) => [...feedbackKeys.all, 'detail', id] as const,
  screenshot: (id: string) => [...feedbackKeys.all, 'screenshot', id] as const,
  activities: (id: string) => [...feedbackKeys.all, 'activities', id] as const,
  filterOptions: () => [...feedbackKeys.all, 'filter-options'] as const,
}

export function useFeedbackList(params: FeedbackListParams, enabled = true) {
  const api = useFeedbackApi()
  return useQuery({
    queryKey: feedbackKeys.list(params),
    enabled,
    queryFn: ({ signal }) =>
      api.get<FeedbackPage>('/eintraege', {
        query: { ...params },
        signal,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  })
}

export type FeedbackAnalysisParams = Pick<
  FeedbackListParams,
  | 'status'
  | 'text'
  | 'areas'
  | 'creatorIds'
  | 'nurUngelesen'
  | 'scopeAreas'
  | 'dateField'
  | 'dateFrom'
  | 'dateTo'
> & { timeGrouping?: FeedbackTimeGrouping; timePage?: number }

export function useFeedbackAnalysis(params: FeedbackAnalysisParams, enabled: boolean) {
  const api = useFeedbackApi()
  return useQuery({
    queryKey: [...feedbackKeys.all, 'analysis', params],
    enabled,
    queryFn: async ({ signal }) => {
      // Load every aggregate group through bounded server pages, without chart pagination.
      const pageSize = 50
      const first = await api.get<FeedbackAnalysis>('/auswertung', {
        query: { ...params, areaPage: 1, creatorPage: 1, pageSize },
        signal,
      })
      const areaPages = Math.ceil(first.areas.total / pageSize)
      const creatorPages = Math.ceil(first.creators.total / pageSize)
      const areas = [...first.areas.items]
      const creators = [...first.creators.items]
      for (let page = 2; page <= Math.max(areaPages, creatorPages); page++) {
        const next = await api.get<FeedbackAnalysis>('/auswertung', {
          query: {
            ...params,
            pageSize,
            areaPage: Math.min(page, Math.max(1, areaPages)),
            creatorPage: Math.min(page, Math.max(1, creatorPages)),
          },
          signal,
        })
        if (page <= areaPages) areas.push(...next.areas.items)
        if (page <= creatorPages) creators.push(...next.creators.items)
      }
      return {
        ...first,
        areas: { ...first.areas, items: areas },
        creators: { ...first.creators, items: creators },
      }
    },
    placeholderData: keepPreviousData,
    refetchInterval: enabled ? 30_000 : false,
  })
}

/** Kleine Abfrage für Hinweise außerhalb der Feedback-Ansicht, etwa die Navigation. */
export function useFeedbackUnreadCount(enabled: boolean) {
  const api = useFeedbackApi()
  return useQuery({
    queryKey: [...feedbackKeys.all, 'unread-count'],
    enabled,
    queryFn: ({ signal }) =>
      api.get<Pick<FeedbackPage, 'unreadCount'>>('/eintraege', {
        query: { page: 1, pageSize: 1, nurUngelesen: true },
        signal,
      }),
    refetchInterval: 30_000,
  })
}

export function useFeedback(id: string | undefined) {
  const api = useFeedbackApi()
  return useQuery({
    queryKey: feedbackKeys.detail(id ?? ''),
    enabled: Boolean(id),
    queryFn: ({ signal }) => api.get<FeedbackEntry>(`/eintraege/${id}`, { signal }),
    refetchInterval: 30_000,
  })
}

export function useFeedbackScreenshot(id: string | undefined, enabled = true) {
  const api = useFeedbackApi()
  return useQuery({
    queryKey: feedbackKeys.screenshot(id ?? ''),
    enabled: Boolean(id) && enabled,
    queryFn: ({ signal }) =>
      api.get<FeedbackScreenshot>(`/eintraege/${id}/screenshot`, { signal }),
  })
}

export function useFeedbackAktivitaeten(id: string | undefined) {
  const api = useFeedbackApi()
  return useQuery({
    queryKey: feedbackKeys.activities(id ?? ''),
    enabled: Boolean(id),
    queryFn: ({ signal }) =>
      api.get<FeedbackActivity[]>(`/eintraege/${id}/aktivitaeten`, { signal }),
    refetchInterval: 30_000,
  })
}

export function useFeedbackStatusAendern(id: string) {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (status: FeedbackStatus) =>
      api.patch<FeedbackEntry>(`/eintraege/${id}/status`, { status }),
    onSuccess: (saved) => {
      queryClient.setQueryData(feedbackKeys.detail(id), saved)
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.all })
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.activities(id) })
    },
  })
}

export function useFeedbackBereichAendern(id: string) {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (modulId: string | null) =>
      api.patch<FeedbackEntry>(`/eintraege/${id}/bereich`, { modulId }),
    onSuccess: (saved) => {
      queryClient.setQueryData(feedbackKeys.detail(id), saved)
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.all })
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.activities(id) })
    },
  })
}

export function useFeedbackKommentarHinzufuegen(id: string) {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (text: string) =>
      api.post<FeedbackActivity>(`/eintraege/${id}/kommentare`, { text }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.root })
    },
  })
}

export function useFeedbackUmsetzungHinzufuegen(id: string) {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: FeedbackImplementationInput) =>
      api.post<FeedbackEntry>(`/eintraege/${id}/umsetzungen`, input),
    onSuccess: (saved) => {
      queryClient.setQueryData(feedbackKeys.detail(id), saved)
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.all })
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.activities(id) })
    },
  })
}

export function useFeedbackInBearbeitungSetzen(id: string) {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (text: string) =>
      api.post<FeedbackEntry>(`/eintraege/${id}/bearbeitungen`, { text }),
    onSuccess: (saved) => {
      queryClient.setQueryData(feedbackKeys.detail(id), saved)
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.all })
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.activities(id) })
    },
  })
}

export function useFeedbackAblehnen(id: string) {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (grund: string) =>
      api.post<FeedbackEntry>(`/eintraege/${id}/ablehnungen`, { grund }),
    onSuccess: (saved) => {
      queryClient.setQueryData(feedbackKeys.detail(id), saved)
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.all })
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.activities(id) })
    },
  })
}

export function useFeedbackZurueckstellen(id: string) {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: FeedbackPostponementInput) =>
      api.post<FeedbackEntry>(`/eintraege/${id}/zurueckstellungen`, input),
    onSuccess: (saved) => {
      queryClient.setQueryData(feedbackKeys.detail(id), saved)
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.all })
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.activities(id) })
    },
  })
}

export function useFeedbackAlsGelesenMarkieren(id: string) {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.post<void>(`/eintraege/${id}/gelesen`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: feedbackKeys.root }),
  })
}

export function useAlleFeedbacksAlsGelesenMarkieren() {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.post<void>('/eintraege/alle-gelesen', {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: feedbackKeys.all }),
  })
}

export function useFeedbackLoeschen(id: string) {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.delete<void>(`/eintraege/${id}`),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: feedbackKeys.detail(id) })
      queryClient.removeQueries({ queryKey: feedbackKeys.screenshot(id) })
      queryClient.removeQueries({ queryKey: feedbackKeys.activities(id) })
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.all })
    },
  })
}
